const chapter = {
  id: 'ecosystem',
  slug: 'ecosystem',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Ecosystem',
        hindi: 'पारिस्थितिक तंत्र',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Easy-Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Ecology', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'intro-structure',
      title: {
        english: 'Structure & Function',
        hindi: 'संरचना और कार्य',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Ecosystem** = functional unit of nature where living organisms interact with non-living environment.',
        hindi: '**पारिस्थितिकी तंत्र** = प्रकृति की कार्यात्मक इकाई जहां जीवित जीव निर्जीव पर्यावरण के साथ बातचीत करते हैं।',
            },
            {
              english: 'Types — **terrestrial** (forest, grassland, desert) and **aquatic** (pond, lake, river, estuary, ocean).',
        hindi: 'प्रकार - **स्थलीय** (जंगल, घास का मैदान, रेगिस्तान) और **जलीय** (तालाब, झील, नदी, मुहाना, महासागर)। **ऊर्ध्वाधर स्तरीकरण** - जंगल में पौधों का स्तरित वितरण (पेड़, झाड़ियाँ, जड़ी-बूटियाँ)।',
            },
            {
              english: '**Vertical stratification** — layered distribution of plants in forest (trees, shrubs, herbs).',
        hindi: '**ऊर्ध्वाधर स्तरीकरण** - जंगल में पौधों का स्तरित वितरण (पेड़, झाड़ियाँ, जड़ी-बूटियाँ)।',
            },
            {
              english: 'Components: **abiotic** (climate, soil, minerals) + **biotic** (producers, consumers, decomposers).',
        hindi: 'घटक: **अजैविक** (जलवायु, मिट्टी, खनिज) + **जैविक** (उत्पादक, उपभोक्ता, डीकंपोजर)।',
            },
            {
              english: 'Functions: **productivity, decomposition, energy flow, nutrient cycling**.',
        hindi: 'कार्य: **उत्पादकता, अपघटन, ऊर्जा प्रवाह, पोषक चक्रण**।',
            },
          ],
        },
      ],
    },
    {
      id: 'productivity',
      title: {
        english: 'Productivity',
        hindi: 'उत्पादकता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Rate of biomass production. Units: **g m⁻² yr⁻¹** or **kcal m⁻² yr⁻¹**.',
        hindi: 'बायोमास उत्पादन की दर। इकाइयाँ: **g m⁻² वर्ष⁻¹** या **kcal m⁻² वर्ष⁻¹**। **GPP (सकल प्राथमिक उत्पादकता)** - प्रकाश संश्लेषण द्वारा उत्पादित कुल कार्बनिक पदार्थ।',
            },
            {
              english: '**GPP (Gross Primary Productivity)** — total organic matter produced by photosynthesis.',
        hindi: '**जीपीपी (सकल प्राथमिक उत्पादकता)** - प्रकाश संश्लेषण द्वारा उत्पादित कुल कार्बनिक पदार्थ।',
            },
            {
              english: '**NPP = GPP - R** (R = respiratory loss). NPP is biomass available to consumers.',
        hindi: '**NPP = GPP - R** (R = respiratory loss)। एनपीपी उपभोक्ताओं के लिए उपलब्ध बायोमास है।',
            },
            {
              english: '**Secondary productivity** — rate of new organic matter formation by consumers.',
        hindi: '**माध्यमिक उत्पादकता** — उपभोक्ताओं द्वारा नए कार्बनिक पदार्थ निर्माण की दर।',
            },
            {
              english: 'Annual NPP of biosphere: ~170 billion tons (dry organic matter); **oceans ~55 billion tons** despite covering 70 % area.',
        hindi: 'जीवमंडल का वार्षिक एनपीपी: ~170 बिलियन टन (शुष्क कार्बनिक पदार्थ); **70% क्षेत्र को कवर करने के बावजूद महासागर ~55 बिलियन टन**।',
            },
          ],
        },
      ],
    },
    {
      id: 'decomposition',
      title: {
        english: 'Decomposition',
        hindi: 'अपघटन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Decomposers (bacteria + fungi) break **detritus** (dead plant remains, animal remains, faecal matter) into inorganic substances.',
        hindi: 'डीकंपोजर (बैक्टीरिया + कवक) **डिटरिटस** (मृत पौधे के अवशेष, जानवरों के अवशेष, मल पदार्थ) को अकार्बनिक पदार्थों में तोड़ देते हैं।',
            },
            {
              english: 'Steps:',
        hindi: 'कदम:',
            },
            {
              english: '• **Fragmentation** — by detritivores (e.g. earthworm).',
        hindi: '• **विखंडन** - डिट्रिटिवोर्स (जैसे केंचुआ) द्वारा।',
            },
            {
              english: '• **Leaching** — water-soluble nutrients percolate down to lower layers (become unavailable salts).',
        hindi: '• **लीचिंग** - पानी में घुलनशील पोषक तत्व निचली परतों में चले जाते हैं (अनुपलब्ध लवण बन जाते हैं)।',
            },
            {
              english: '• **Catabolism** — bacterial/fungal enzymes degrade detritus → simple inorganic substances.',
        hindi: '• **अपचय** - bacterial/fungal एंजाइम अपरद को नष्ट करते हैं → सरल अकार्बनिक पदार्थ।',
            },
            {
              english: '• **Humification** — accumulation of dark, amorphous, colloidal **humus** (highly resistant to microbial action; reservoir of nutrients).',
        hindi: '• **ह्यूमिफिकेशन** - गहरे, अनाकार, कोलाइडल **ह्यूमस** का संचय (माइक्रोबियल कार्रवाई के लिए अत्यधिक प्रतिरोधी; पोषक तत्वों का भंडार)।',
            },
            {
              english: '• **Mineralisation** — humus further degraded → release of inorganic nutrients (CO₂, water, nutrients).',
        hindi: '• **खनिजीकरण** - ह्यूमस का और अधिक ह्रास हुआ → अकार्बनिक पोषक तत्वों (CO₂, पानी, पोषक तत्व) का निकलना। दर इस पर निर्भर करती है: **डिटरिटस का रसायन** ( धीमा; तेज), **तापमान** (, **मिट्टी की नमी** (गर्म + नम अनुकूल), **ऑक्सीजन** (एरोबिक तेज)।',
            },
            {
              english: 'Rate depends on: **chemistry of detritus** (lignin/chitin slow; nitrogen/sugar rapid), **temperature** (warm = fast), **soil moisture** (warm + moist favours), **oxygen** (aerobic faster).',
        hindi: 'दर इस पर निर्भर करती है: **डिटरिटस का रसायन** (lignin/chitin धीमा; nitrogen/sugar तेज), **तापमान** (warm = fast), **मिट्टी की नमी** (गर्म + नम अनुकूल), **ऑक्सीजन** (एरोबिक तेज)।',
            },
          ],
        },
      ],
    },
    {
      id: 'energy-flow',
      title: {
        english: 'Energy Flow',
        hindi: 'ऊर्जा प्रवाह',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Sun is sole energy source. Less than **50 %** of incident solar radiation is **PAR** (photosynthetically active radiation).',
        hindi: 'सूर्य एकमात्र ऊर्जा स्रोत है। आपतित सौर विकिरण का **50%** से कम **PAR** (प्रकाश संश्लेषक रूप से सक्रिय विकिरण) है।',
            },
            {
              english: 'Plants capture only **2–10 %** of PAR.',
        hindi: 'पौधे केवल **2-10%** PAR ग्रहण करते हैं।',
            },
            {
              english: '**Trophic levels** — producers (T1) → primary consumers/herbivores (T2) → secondary (T3) → tertiary (T4) → quaternary (T5).',
        hindi: '**ट्रॉफिक स्तर** - उत्पादक (T1) → प्राथमिक consumers/herbivores (T2) → द्वितीयक (T3) → तृतीयक (T4) → चतुर्धातुक (T5)।',
            },
            {
              english: '**10 % law (Lindeman, 1942)** — only 10 % of energy is transferred to next trophic level; rest lost as heat.',
        hindi: '**10% कानून (लिंडमैन, 1942)** - केवल 10% ऊर्जा अगले पोषी स्तर पर स्थानांतरित होती है; बाकी गर्मी के रूप में खो गया.',
            },
            {
              english: '**Food chain** — linear sequence. Two types:',
        hindi: '**खाद्य शृंखला** — रैखिक अनुक्रम। दो प्रकार:',
            },
            {
              english: '• **Grazing food chain (GFC)** — starts with producers (Grass → Goat → Man).',
        hindi: '• **चराई खाद्य श्रृंखला (जीएफसी)** - उत्पादकों (घास → बकरी → आदमी) से शुरू होती है।',
            },
            {
              english: '• **Detritus food chain (DFC)** — starts with dead organic matter (in forest most energy flows via DFC).',
        hindi: '• **डेट्रिटस खाद्य श्रृंखला (डीएफसी)** - मृत कार्बनिक पदार्थ से शुरू होती है (जंगल में अधिकांश ऊर्जा डीएफसी के माध्यम से बहती है)।',
            },
            {
              english: '**Food web** — interconnected food chains.',
        hindi: '**खाद्य वेब** — परस्पर जुड़ी खाद्य शृंखलाएँ।',
            },
            {
              english: 'Standing crop = biomass at any time.',
        hindi: 'खड़ी फसल = किसी भी समय बायोमास।',
            },
          ],
        },
      ],
    },
    {
      id: 'pyramids',
      title: {
        english: 'Ecological Pyramids',
        hindi: 'पारिस्थितिक पिरामिड',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Pyramid', 'Grassland', 'Pond', 'Forest', 'Parasitic chain'],
          rows: [
            ['Number', 'Upright', 'Upright', 'Inverted', 'Inverted'],
            ['Biomass', 'Upright', 'Inverted (phytoplankton small/short-lived)', 'Upright', '—'],
            ['Energy', 'Always upright', 'Always upright', 'Always upright', 'Always upright'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Pyramid of **energy is always upright** (10 % law — energy decreases with each level).',
        hindi: '**ऊर्जा का पिरामिड हमेशा सीधा होता है** (10% नियम - प्रत्येक स्तर के साथ ऊर्जा घटती है)।',
            },
            {
              english: 'Pyramid of biomass in **sea/pond is inverted** because phytoplankton biomass at any moment is less than zooplankton.',
        hindi: '**sea/pond में बायोमास का पिरामिड उल्टा है** क्योंकि किसी भी समय फाइटोप्लांकटन बायोमास ज़ोप्लांकटन से कम होता है।',
            },
            {
              english: 'Limitation — does not account for same species at multiple trophic levels; saprophytes ignored.',
        hindi: 'सीमा - एकाधिक पोषी स्तरों पर एक ही प्रजाति के लिए जिम्मेदार नहीं है; सैप्रोफाइट्स को नजरअंदाज कर दिया गया।',
            },
          ],
        },
      ],
    },
    {
      id: 'succession',
      title: {
        english: 'Ecological Succession',
        hindi: 'पारिस्थितिक अनुक्रमण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Gradual & predictable change in species composition of an area until a **climax community** (steady state) develops.',
        hindi: '**चरमोत्कर्ष समुदाय** (स्थिर अवस्था) विकसित होने तक किसी क्षेत्र की प्रजातियों की संरचना में क्रमिक और पूर्वानुमानित परिवर्तन। **अग्रणी प्रजाति** - उपनिवेश स्थापित करने वाली पहली। **सेरे(s)** - क्रमिक समुदायों का संपूर्ण अनुक्रम।',
            },
            {
              english: '**Pioneer species** — first to colonise.',
        hindi: '**अग्रणी प्रजाति** - उपनिवेश स्थापित करने वाली पहली प्रजाति।',
            },
            {
              english: '**Sere(s)** — entire sequence of successional communities.',
        hindi: '**सेरे(ओं)** - क्रमिक समुदायों का संपूर्ण अनुक्रम।',
            },
            {
              english: 'Two types:',
        hindi: 'दो प्रकार:',
            },
            {
              english: '• **Primary succession** — on bare/lifeless area (newly cooled lava, bare rock, sand dunes, glacier). Very slow (~1000 yrs).',
        hindi: '• **प्राथमिक उत्तराधिकार** — bare/lifeless क्षेत्र पर (नया ठंडा हुआ लावा, नंगी चट्टान, रेत के टीले, ग्लेशियर)। बहुत धीमी (~1000 वर्ष)।',
            },
            {
              english: '• **Secondary succession** — area where community existed but was lost (abandoned farmland, burned forest). Faster, soil already present.',
        hindi: '• **द्वितीयक उत्तराधिकार** - वह क्षेत्र जहां समुदाय मौजूद था लेकिन नष्ट हो गया (परित्यक्त खेत, जले हुए जंगल)। तेज़, मिट्टी पहले से मौजूद है।',
            },
            {
              english: 'Hydrarch (water → land) and Xerarch (dry → mesic) — both progress to **mesic** climax.',
        hindi: 'हाइड्रार्क (जल → भूमि) और ज़ेरार्क (शुष्क → मेसिक) - दोनों **मेसिक** चरमोत्कर्ष की ओर बढ़ते हैं।',
            },
            {
              english: 'Hydrarch sequence: phytoplankton → submerged → free-floating → reed-swamp → marsh-meadow → scrub → forest.',
        hindi: 'हाइड्रार्क अनुक्रम: फाइटोप्लांकटन → जलमग्न → मुक्त-फ्लोटिंग → ईख-दलदल → दलदल-घास का मैदान → झाड़ी → जंगल।',
            },
            {
              english: 'Xerarch sequence: lichens → mosses → herbs → shrubs → trees.',
        hindi: 'ज़ेरार्क अनुक्रम: लाइकेन → काई → जड़ी-बूटियाँ → झाड़ियाँ → पेड़।',
            },
          ],
        },
      ],
    },
    {
      id: 'nutrient-cycling',
      title: {
        english: 'Nutrient Cycling',
        hindi: 'पोषक चक्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Movement of nutrients through biotic and abiotic components → **biogeochemical cycles**.',
        hindi: 'जैविक और अजैविक घटकों के माध्यम से पोषक तत्वों की गति → **जैव भू-रासायनिक चक्र**।',
            },
            {
              english: 'Two types:',
        hindi: 'दो प्रकार:',
            },
            {
              english: '• **Gaseous** — reservoir is atmosphere (carbon, nitrogen).',
        hindi: '• **गैसीय** - भंडार वायुमंडल (कार्बन, नाइट्रोजन) है।',
            },
            {
              english: '• **Sedimentary** — reservoir is Earth\'s crust (phosphorus, sulphur).',
        hindi: '• **तलछटी** — जलाशय पृथ्वी की पपड़ी (फास्फोरस, सल्फर) है। **कार्बन चक्र**:',
            },
            {
              english: '**Carbon cycle**:',
        hindi: '**कार्बन चक्र**:',
            },
            {
              english: '• 71 % carbon dissolved in oceans; atmosphere has 1 %.',
        hindi: '• 71% कार्बन महासागरों में घुल गया; वायुमंडल में 1% है.',
            },
            {
              english: '• Annual fixation by photosynthesis: ~4 × 10¹³ kg of C.',
        hindi: '• प्रकाश संश्लेषण द्वारा वार्षिक निर्धारण: ~4 × 10¹³ किलोग्राम C.',
            },
            {
              english: '• Returned via respiration, decomposition, burning of wood/forest fires/fuel, volcanism.',
        hindi: '• श्वसन, अपघटन, wood/forest fires/fuel के जलने, ज्वालामुखी के माध्यम से लौटा। **फॉस्फोरस चक्र**:',
            },
            {
              english: '**Phosphorus cycle**:',
        hindi: '**फॉस्फोरस चक्र**:',
            },
            {
              english: '• Reservoir = rock (apatite).',
        hindi: '• जलाशय = चट्टान (एपेटाइट)।',
            },
            {
              english: '• Released by weathering → soil solution → absorbed by plants → consumers → decomposers.',
        hindi: '• अपक्षय → मिट्टी के घोल → पौधों द्वारा अवशोषित → उपभोक्ताओं → डीकंपोजर द्वारा जारी।',
            },
            {
              english: '• No respiratory release; **no atmospheric component** (sedimentary).',
        hindi: '• कोई श्वसन रिलीज नहीं; **कोई वायुमंडलीय घटक नहीं** (तलछटी)।',
            },
          ],
        },
      ],
    },
    {
      id: 'services',
      title: {
        english: 'Ecosystem Services',
        hindi: 'पारिस्थितिक तंत्र सेवाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Products of ecosystem processes valuable to humans — pollination, climate regulation, flood control, soil formation, recreation.',
        hindi: 'मानव के लिए मूल्यवान पारिस्थितिकी तंत्र प्रक्रियाओं के उत्पाद - परागण, जलवायु विनियमन, बाढ़ नियंत्रण, मिट्टी निर्माण, मनोरंजन।',
            },
            {
              english: 'Robert Costanza et al. estimated value of all ecosystem services = **~US $33 trillion / yr** (~ 2× global GNP $18 trillion).',
        hindi: 'रॉबर्ट कोस्टान्ज़ा एट अल। संपूर्ण पारिस्थितिकी तंत्र का अनुमानित मूल्य services = **~US 18 trillion)।',
            },
            {
              english: 'Soil formation: **50 %**, recreation: 10 %, nutrient cycling: 10 %, climate regulation: 6 %, habitat: 5 %.',
        hindi: 'मृदा निर्माण: **50%**, मनोरंजन: 10%, पोषक चक्रण: 10%, जलवायु विनियमन: 6%, आवास: 5%।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{NPP} = \\text{GPP} - R',
      desc: {
        english: 'Net = Gross − Respiration',
        hindi: 'नेट = सकल - श्वसन',
      },
    },
    {
      latex: '\\text{Energy transfer} \\approx 10\\% \\text{ per trophic level (Lindeman)}',
      desc: {
        english: '',
        hindi: 'ऊर्जा स्थानांतरण 10\\% प्रति पोषी स्तर (लिंडमैन)',
      },
    },
    {
      latex: '\\text{PAR} \\approx 50\\% \\text{ of incident solar radiation}',
      desc: {
        english: 'Plants capture 2-10 %',
        hindi: 'पौधे 2-10% पर कब्जा करते हैं',
      },
    },
    {
      latex: '\\text{Pyramid of energy: always upright}',
      desc: {
        english: '',
        hindi: 'ऊर्जा का पिरामिड: सदैव सीधा',
      },
    },
    {
      latex: '\\text{Pyramid of biomass in pond: inverted}',
      desc: {
        english: '',
        hindi: 'तालाब में बायोमास का पिरामिड: उल्टा',
      },
    },
    {
      latex: '\\text{Hydrarch \\& Xerarch} \\rightarrow \\text{mesic climax}',
      desc: {
        english: '',
        hindi: 'हाइड्रार्क \\& ज़ेरार्क मेसिक चरमोत्कर्ष',
      },
    },
    {
      latex: '\\text{Phosphorus cycle = sedimentary, no gaseous phase}',
      desc: {
        english: '',
        hindi: 'फास्फोरस चक्र = तलछटी, कोई गैसीय चरण नहीं',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Energy',
      problem: {
        english: '10 % law of energy transfer was given by:',
        hindi: 'ऊर्जा स्थानांतरण का 10% नियम किसके द्वारा दिया गया था:',
      },
      answer: {
        english: 'Lindeman (1942)',
        hindi: 'लिंडमैन (1942)',
      },
      solution: {
        english: 'Only 10 % energy passes to next trophic level.',
        hindi: 'केवल 10% ऊर्जा अगले पोषी स्तर तक पहुंचती है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Pyramid',
      problem: {
        english: 'Pyramid of biomass in a pond is:',
        hindi: 'एक तालाब में बायोमास का पिरामिड है:',
      },
      answer: {
        english: 'Inverted',
        hindi: 'उलटा',
      },
      solution: {
        english: 'Phytoplankton biomass at any moment is less than zooplankton.',
        hindi: 'फाइटोप्लांकटन बायोमास किसी भी समय ज़ोप्लांकटन से कम होता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Decomposition',
      problem: {
        english: 'Accumulation of dark coloured amorphous humus is called:',
        hindi: 'गहरे रंग के अनाकार ह्यूमस का संचय कहलाता है:',
      },
      answer: {
        english: 'Humification',
        hindi: 'आर्द्रीकरण',
      },
      solution: {
        english: 'Highly resistant to microbial action; nutrient reservoir.',
        hindi: 'माइक्रोबियल क्रिया के प्रति अत्यधिक प्रतिरोधी; पोषक भंडार.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Cycle',
      problem: {
        english: 'Which biogeochemical cycle has no gaseous phase?',
        hindi: 'किस जैव-भू-रासायनिक चक्र में कोई गैसीय चरण नहीं है?',
      },
      answer: {
        english: 'Phosphorus cycle',
        hindi: 'फास्फोरस चक्र',
      },
      solution: {
        english: 'Reservoir is rock (apatite); sedimentary.',
        hindi: 'जलाशय चट्टानी (एपेटाइट) है; तलछटी.',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Succession',
      problem: {
        english: 'Pioneer species on bare rock are:',
        hindi: 'नंगी चट्टान पर अग्रणी प्रजातियाँ हैं:',
      },
      answer: {
        english: 'Lichens',
        hindi: 'लाइकेन',
      },
      solution: {
        english: 'Xerarch primary succession begins with crustose lichens.',
        hindi: 'ज़ेरार्क प्राथमिक उत्तराधिकार क्रस्टोज़ लाइकेन से शुरू होता है।',
      },
    },
  ],
  next: {
    slug: 'biodiversity-conservation',
    title: 'Biodiversity and Conservation',
  },
};

export default chapter;
